// eslint-disable no-useless-escape
import { PlatformName } from '../services';

export const sanitizeFileName = (fileName: string): string =>
	fileName
		.slice(0, -5)
		.replace(/[\. ,:-_]+/g, '')
		.toLowerCase();

export const getTypeofProperty = <T, K extends keyof T>(o: T, name: K) => typeof o[name];

export const countDecimals = (value: any) => {
	if (isNaN(value)) {
		return 0;
	}
	const numberAsString = value.toString();

	if (value && numberAsString.lastIndexOf('.') > -1) {
		return numberAsString.split('.')[1].length;
	}
	return 0;
};

export const countNumberOfDigits = (value: any) => {
	if (isNaN(value) || !value) {
		return 0;
	}

	const numberAsString = value.toString();

	// If value is float, then extract 1 from the length of the number (corresponding to the .)
	// If the number is negative, having '-' sign in front, extract 1 from length.
	// If the number is int, remove one value if the number is negative.
	return numberAsString.lastIndexOf('.') > -1
		? numberAsString.length + (Math.sign(value) < 1 ? -1 : 0) - 1
		: numberAsString.length + (Math.sign(value) < 1 ? -1 : 0);
};

export const removeFileExtension = (fileName: string, extensionToRemove: string) => {
	const fileNameExtension = readFileExtension(fileName).toLowerCase();

	if (fileNameExtension === extensionToRemove) {
		return fileName.split('.').slice(0, -1).join('.');
	}

	return fileName;
};

export const readFileExtension = (fileName: string) => fileName.split('.').pop();

export const stringContainsNumber = (str: string): boolean => /\d/.test(str);

export const incrementSampleNumber = (str: string): string => {
	// if string does not end with a number or does not contain a number
	if (!str.match(/\d+$/) || !stringContainsNumber(str)) {
		return str + '1';
	}

	// increment number at the end of the string
	return str.replace(/\d+$/, (suffixNumber: string) => {
		let pad = 0;

		// some strings end with a number like this 00123
		// we check if lenght of this number remain the same if we parse it to number
		if (suffixNumber.length !== parseInt(suffixNumber, 10).toString().length) {
			pad = suffixNumber.length;
		}

		return Number(parseInt(suffixNumber, 10) + 1)
			.toString()
			.padStart(pad, '0');
	});
};

export const replaceAll = (inputString: string, valueToReplace: string, replacementValue: string): string => {
	const searchRegExp = new RegExp(valueToReplace, 'g'); // Throws SyntaxError
	return inputString.replace(searchRegExp, replacementValue);
};

export const replaceMultiple = (value: string, valueReplacements: { [key: string]: string }): string => {
	Object.entries(valueReplacements).forEach(([rezervedWord, reservedWordReplacement]) => {
		value = replaceAll(value, reservedWordReplacement, rezervedWord);
	});

	return value;
};

export const capitalizeFirstLetter = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

export const countNumberDecimals = (no: number, decimalSeparator: string): number => {
	if (Math.floor(no) === no) {
		return 0;
	}
	return no.toString().split(decimalSeparator)[1].length || 0;
};

export const convertStringToArray = (value: string): string[] => {
	const emptyArray = [];
	return [...emptyArray, value];
};

export const delayExecution = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const regexPatternForConnectionUrl = (): string =>
	'^(https?:\\/\\/)?' + // validate protocol
	'((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
	'((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
	'(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
	'(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
	'(\\#[-a-z\\d_]*)?$';

// this method retrieves the max breakpoint where the dropdown can stretch to when open
// first step is to read the height of the header element and assign a default value of 64px (current header height set),
// in case the reading fails.
// Based on the platform the app is running, a notch height is assignedȘ 48 for ios, 0 for others. However, there are ios device
// which don't have notches, meaning that if the docHeaderHeight > 64 (has extra layer added - notch), then headerArea will
// be the result of extracting notch from height of header element.
// Last but not least, the returned result will be a value from 0 - 1, based on a calculation (+0.01 is just a fine tuning)
export const getMaxBreakpoint = (platformName: PlatformName): number => {
	let headerHeight = 64;

	if (document.getElementById('app-header')) {
		headerHeight = document.getElementById('app-header')?.clientHeight;
	}

	const notchHeight: number = platformName === PlatformName.IOS ? 48 : 0;
	const headerArea = headerHeight > 64 ? headerHeight - notchHeight : headerHeight;

	return 1 - headerArea / (window.innerHeight / 100) / 100 + 0.01;
};

export const sanitizePositionProperty = (value: string | number, platformName: PlatformName): string | number => {
	let fieldValue: string | number;
	if (platformName === PlatformName.IOS) {
		fieldValue = String(value).replaceAll(',', '.');
	} else {
		if (isNaN(Number(value))) {
			return;
		}

		fieldValue = value;
	}

	return fieldValue;
};

export function lowercaseKeys(obj: any): any {
	if (obj === null || obj === undefined) {
		return obj;
	}

	if (Array.isArray(obj)) {
		return obj.map((item) => lowercaseKeys(item));
	}

	if (typeof obj === 'object') {
		const newObj: any = {};
		Object.keys(obj).forEach((key) => {
			const lowerKey = key.toLowerCase();
			newObj[lowerKey] = lowercaseKeys(obj[key]);
		});
		return newObj;
	}

	return obj;
}